import { useNavigate } from "react-router-dom";

const PrivateLessonsPlans = () => {
  const navigate = useNavigate();

  const planCategories = [
    {
      title: "Private Home Visit Packages",
      description: "Personalized swimming lessons in your own pool",
      plans: [
        {
          id: "basic",
          title: "Basic Private Lessons",
          description: "Affordable & flexible - perfect for beginners.",
          price: "1500 AED",
          sessions: "10 sessions",
          features: [
            "AED 150 per session",
            "AED 1500 for 10 sessions (valid for 90 days)",
            "30 min per session (1-on-1)",
            "Great for beginner level"
          ],
          buttonText: "Select Basic Private Lessons",
          buttonVariant: "gray"
        },
        {
          id: "premium",
          title: "Premium Private Lessons",
          description: "Enhanced swimming experience with extra time and focus.",
          price: "2000 AED",
          sessions: "10 sessions",
          features: [
            "AED 200 per session",
            "AED 2000 for 10 sessions (valid for 90 days)",
            "45 min per session (1-on-1)",
            "Most booked, ideal for skill development"
          ],
          buttonText: "Select Premium Private Lessons",
          buttonVariant: "blue",
          popular: true
        },
        {
          id: "intensive",
          title: "Intensive Private Program",
          description: "Comprehensive and goal-focused swimming development.",
          price: "2500 AED",
          sessions: "10 sessions",
          features: [
            "AED 250 per session",
            "AED 2500 for 10 sessions (valid for 90 days)",
            "60 min per session (1-on-1)",
            "High-performance private coaching – perfect for advanced swimmers & competition prep"
          ],
          buttonText: "Select Intensive Private Program",
          buttonVariant: "gray"
        }
      ]
    },
    {
      title: "Sibling Packages",
      description: "Save more when your kids swim together!",
      plans: [
        {
          id: "sibling",
          title: "Sibling Package",
          description: "Save more when your kids swim together!",
          price: "200 AED",
          sessions: "2 kids",
          features: [
            "AED 200 per session (for 2 kids)",
            "45 min per session (2-on-1)",
            "For siblings or close friends",
            "Fun, engaging & cost-effective way to learn together"
          ],
          buttonText: "Select Sibling Package",
          buttonVariant: "green"
        }
      ]
    }
  ];

  const handleSelectPlan = (plan) => {
    navigate('/enrollment-form', { state: { selectedPlan: plan } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-6 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-arrow-left h-5 w-5 mr-2"
            >
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            Back to Services
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Private Swimming Lessons</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your pace. Personalized swimming lessons tailored to your needs.
            </p>
          </div>
        </div>

        {planCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{category.title}</h2>
              <p className="text-gray-600">{category.description}</p>
            </div>
            <div className={`grid ${category.plans.length === 1 ? 'md:grid-cols-1 max-w-md' : 'md:grid-cols-3'} gap-8 max-w-6xl mx-auto`}>
              {category.plans.map((plan) => (
                <div 
                  key={plan.id}
                  className={`relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 cursor-pointer border ${plan.popular ? 'border-2 border-blue-500 ring-4 ring-blue-100' : 'border-gray-200'}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="lucide lucide-crown h-4 w-4 mr-1"
                        >
                          <path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
                        </svg>
                        Most Popular
                      </div>
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.title}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-600 ml-2">/ {plan.sessions}</span>
                    </div>
                  </div>
                  <div className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="bg-green-100 rounded-full p-1 mr-3">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-check h-4 w-4 text-green-600"
                          >
                            <path d="M20 6 9 17l-5-5"></path>
                          </svg>
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <button 
                    onClick={() => handleSelectPlan(plan)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                      plan.buttonVariant === 'blue' 
                        ? 'bg-blue-600 text-white hover:bg-blue-700' 
                        : plan.buttonVariant === 'green'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    {plan.buttonText}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="text-center mt-16">
          <p className="text-gray-600 mb-4">Need help choosing the right package?</p>
          <button className="text-blue-600 hover:text-blue-700 font-semibold">
            Contact our education consultants
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivateLessonsPlans;